import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KeyRound, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";

const PRO_TOKEN_KEY = "altiora_pro_token";
const PRO_KEY_KEY = "altiora_pro_key";

export default function ProLogin() {
  const [proKey, setProKey] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!proKey || code.length !== 6) {
      toast.error("Identifiant pro + code à 6 chiffres requis.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/partner-pro/claim/${proKey}`, {
        claim_code: code.toUpperCase(),
        email: email || null,
      });
      localStorage.setItem(PRO_TOKEN_KEY, data.token);
      localStorage.setItem(PRO_KEY_KEY, data.pro_key);
      toast.success(`Bienvenue ${data.pro?.first_name || ""} · session active 30 jours.`);
      navigate("/pro");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Échec de la connexion.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md rounded-2xl border border-[#E4E1D8] bg-white shadow-xl overflow-hidden altiora-scale-in">
        <div className="px-8 pt-8 pb-6 border-b border-[#E4E1D8] bg-gradient-to-br from-white to-[#FBF6EA]">
          <div className="text-xs font-mono uppercase tracking-[0.24em] text-[#7A5B14]">
            ALTIORA · Espace Partenaire Pro
          </div>
          <div className="mt-1 font-display text-2xl text-[#1F2937] tracking-tight">
            Centre de pilotage des interventions
          </div>
          <div className="mt-2 text-sm text-[#5B5B62]">
            File d'attente intelligente triée par impact sur la trajectoire de chaque personne.
          </div>
        </div>

        <form onSubmit={submit} className="px-8 py-6 space-y-4" data-testid="pro-login-form">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-1.5">
              Identifiant professionnel
            </label>
            <input
              data-testid="pro-login-key"
              value={proKey}
              onChange={(e) => setProKey(e.target.value.trim())}
              placeholder="pro_ca_mtl_career_dubois"
              className="w-full px-3 py-2.5 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm text-[#1F2937] placeholder:text-[#8B8B8F] focus:outline-none focus:border-[#0B2545] altiora-transition font-mono"
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-1.5">
              Code de réclamation (6 caractères)
            </label>
            <input
              data-testid="pro-login-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="ABC123"
              maxLength={6}
              className="w-full px-3 py-2.5 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-lg text-[#1F2937] placeholder:text-[#8B8B8F] focus:outline-none focus:border-[#0B2545] altiora-transition font-mono tracking-widest text-center"
            />
            <div className="mt-1.5 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
              <ShieldCheck className="inline h-3 w-3 mr-1 text-[#059669]" strokeWidth={2} />
              Fourni par ALTIORA lors de votre référencement
            </div>
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-[#0B2545] mb-1.5">
              Email (optionnel)
            </label>
            <input
              data-testid="pro-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="pro@cabinet.com"
              className="w-full px-3 py-2.5 rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-sm text-[#1F2937] placeholder:text-[#8B8B8F] focus:outline-none focus:border-[#0B2545] altiora-transition"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="pro-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-3 rounded-md altiora-transition shadow-sm disabled:opacity-60"
          >
            <KeyRound className="h-4 w-4" strokeWidth={2} />
            {loading ? "Ouverture…" : "Accéder à mon espace"}
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </button>

          <div className="pt-4 border-t border-[#E4E1D8] text-[11px] text-[#5B5B62]">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-3 w-3 text-[#C5A880]" strokeWidth={2} />
              <span className="font-mono uppercase tracking-widest text-[#7A5B14]">Démo</span>
            </div>
            Pas encore de code ? Les fondateurs ALTIORA peuvent générer les codes depuis
            <span className="font-mono"> /partner-pro/demo-codes</span>.
          </div>
        </form>
      </div>
    </div>
  );
}
